import React, { FC, ReactNode, useState, useEffect, useRef } from 'react'
import { subscribe } from 'react-contextual'
import { sumBy } from 'lodash-es'
import { isEmpty, mergeRight, path, pathOr } from 'ramda'
import { format, isValid as isDateValid } from 'date-fns'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import { arrayUtils, currencyUtils, formUtils } from '../../../../utils'
import { PartySearch } from '../../../../views/containers'
import { Button, DatePicker, FormField, FormLoader, SaveChangesModal, Segment } from '../../../../views/components'
import CalendarOff from '../../../../views/components/atoms/Svgs/CalendarOff'
import AdhocInvoiceProvider from '../../AdhocInvoiceProvider'
import { apiStateSelectors } from '../../../api/apiState'
import { invoiceApiEvents } from '../../../api/invoice'
import PaymentAllocation from './PaymentAllocation/PaymentAllocation'
import styles from './AdhocInvoiceForm.module.scss'
import { defaultAgencyInvoiceTypes, depositInvoiceTypes } from 'modules/api/invoice/constants'
import { userApiSelectors } from '../../../api/user'
import { $TSFixMe } from 'types/ts-migrate'
import { portfolioApiEvents, portfolioApiSelectors } from 'modules/api/portfolio'
import { walletApiEvents } from 'modules/api/wallet'
import { partyApiSelectors } from 'modules/api/party'
import { transformDepositBeneficiaryForTransferChange } from 'views/components/organisms/FormFragments/FundDistribution/DepositBeneficiary/depositBeneficiaryUtils'
import { agencyApiSelectors } from 'modules/api/agency'
import { invoiceTypes } from 'utils/invoiceTypes'

interface InvoiceTypeOption {
  name?: string
  label?: string
  value: string
}

interface BeneficiaryValue {
  amount?: number | string
  partyId?: string
  partyTag?: string
  reference?: string
  vat?: boolean
  beneficiaryTag?: string
  transfer?: boolean
  name?: string
}

interface Beneficiary {
  beneficiary: {
    type?: string
    value: BeneficiaryValue
  }
}

interface AdhocInvoiceCustomer {
  id: string | null
  text?: string
  partyTag?: string
  secondaryText?: string
  portfolioId?: string | null
  isConsolidatedBalance?: boolean
}

interface AdhocInvoiceFormValues {
  isDepositInvoice?: boolean
  customer: AdhocInvoiceCustomer
  invoiceType: InvoiceTypeOption
  dueDate?: string
  vat?: boolean
  amount?: string
  beneficiaries: Beneficiary[]
  description?: string
  portfolioId?: string | null
  id?: string
}

interface PartyTag {
  value?: string
  label?: string
}

interface AdhocInvoiceFormProps {
  updateInvoice: (id: string, payload: Partial<AdhocInvoiceFormValues>) => void
  currentAgencyId: string
  fetchInvoiceTypes: () => void
  invoiceTypesForSelect?: InvoiceTypeOption[]
  closeInvoice: (tab?: string) => void
  getPartyNameById: (id?: string | null) => string
  initialValues: AdhocInvoiceFormValues
  onSubmit: (payload: ReturnType<typeof transformFormValues>) => void
  createInvoice: (values: ReturnType<typeof transformFormValues>) => void
  partyTags?: PartyTag[]
  isSubmitting?: boolean
}

const validationSchema = yup.object().shape({
  customer: yup.object().test('customer', function (customer) {
    if (customer.id === null) {
      return this.createError({
        path: this.path,
        message: 'Required',
      })
    }
    return true
  }),
  invoiceType: yup.object().test('invoiceType', function (invoiceType) {
    if (invoiceType.value === null) {
      return this.createError({
        path: this.path,
        message: 'Required',
      })
    }
    return true
  }),
  amount: yup.number().required('Required'),
  beneficiaries: yup
    .array(
      yup.object().shape({
        amount: yup.number(),
        balance: yup.number(),
        beneficiaryCategory: yup.string(),
        id: yup.string(),
        partyId: yup.string(),
        partyTag: yup.string(),
        reference: yup.string(),
        vat: yup.boolean(),
      }),
    )
    .test('beneficiaries', function (beneficiaries) {
      const beneficiariesTotal = sumBy(beneficiaries, (b: any) => b.beneficiary.value.amount)
      if (beneficiariesTotal > this.parent.amount) {
        return this.createError({
          path: this.path,
          message: `Beneficiary payments can't exceed invoice total of ${currencyUtils.formatCurrency(
            this.parent.amount,
          )}`,
        })
      }
      return true
    }),
})

const ButtonContainer: FC<{ beforeButton: ReactNode; children: ReactNode }> = ({ beforeButton, children }) => (
  <footer>
    <div className={styles['form-footer']}>
      {beforeButton}
      <div className={styles['action-button']}>{children}</div>
    </div>
  </footer>
)

const transformFormValues = (values: any, sendAfterCreate = true) => {
  const payload = {
    ...values,
    amount: parseFloat(values.amount),
    customer: values.customer.id,
    customerTag: values.customer.isConsolidatedBalance ? 'Consolidated' : values.customer.partyTag,
    portfolioId: values.customer.isConsolidatedBalance ? null : values.customer.portfolioId,
    invoiceType: values.invoiceType.value,
    beneficiaries: values.beneficiaries.map((b: any) => ({
      ...b,
      amount: parseFloat(b.amount),
    })),
  }
  if (path(['customer', 'portfolioId'], values)) {
    payload.portfolioId = values.customer.portfolioId
  }
  return {
    values: payload,
    sendAfterCreate,
  }
}

const AdhocInvoiceForm = ({
  invoiceTypesForSelect,
  getPartyNameById,
  partyTags,
  initialValues,
  onSubmit,
  createInvoice,
  closeInvoice,
  fetchInvoiceTypes,
  updateInvoice,
  currentAgencyId,
  isSubmitting,
}: AdhocInvoiceFormProps) => {
  useEffectOnce(() => {
    fetchInvoiceTypes()
  })

  const dispatch = useDispatch()
  const allParties = useSelector(partyApiSelectors.getParties)

  // Fetch balance data for all parties so consolidated balance shows in search.
  // Parties load asynchronously and incrementally, so re-run as they arrive but
  // only request each party's balance once.
  const requestedBalances = useRef<Set<string>>(new Set())
  useEffect(() => {
    Object.keys(allParties).forEach(partyId => {
      if (!requestedBalances.current.has(partyId)) {
        requestedBalances.current.add(partyId)
        dispatch(walletApiEvents.balance_request({ partyId }))
      }
    })
  }, [allParties, dispatch])

  const [savePromptOpen, setSavePromptOpen] = useState(false)
  const [beneficiariesInState, setBeneficiariesInState] = useState(initialValues.beneficiaries)

  const createInvoiceEvent = invoiceApiEvents.createOnceOffInvoice_request
  const generalErrors = useSelector(state => apiStateSelectors.getGeneralFormErrorsByEvent(state)(createInvoiceEvent))
  const fieldErrors = useSelector(state => apiStateSelectors.getFormFieldErrorsByEvent(state)(createInvoiceEvent))
  const isReadonly = useSelector(userApiSelectors.isReadOnlyRole)
  const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

  const getPrimaryLandlordByPortfolioId = useSelector(portfolioApiSelectors.getPrimaryLandlordByPortfolioId)
  const getCurrentAgencyGlobalVatEnabled = useSelector(agencyApiSelectors.getCurrentAgencyGlobalVatEnabled)
  const currentAgencyName = useSelector(agencyApiSelectors.getCurrentAgencyName)
  const [isDefaultAgencyPaymentRule, setIsDefaultAgencyPaymentRule] = useState(false)

  return (
    <Formik
      validateOnBlur
      validateOnChange
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values: $TSFixMe) => {
        const payload = transformFormValues(values)
        onSubmit(payload)
      }}
    >
      {({ handleSubmit, values, setFieldValue, setFieldTouched, errors, resetForm, submitCount, touched, isValid }) => {
        const handleSaveConfirmation = (): void => {
          const { id, ...payload } = values
          updateInvoice(id, payload)
          setSavePromptOpen(false)
          closeInvoice('drafts')
        }

        const handleSave = (values: any) => {
          createInvoice(values)
          closeInvoice('drafts')
        }

        const handleDiscardConfirmation = (): void => {
          resetForm()
          setSavePromptOpen(false)
          closeInvoice('drafts')
        }

        const handleNewBeneficiary = (beneficiary: any, index: number): void => {
          const vatApplied =
            beneficiary.beneficiary.value.partyTag === 'Agency' && getCurrentAgencyGlobalVatEnabled ? true : false
          setFieldValue(
            'beneficiaries',
            arrayUtils.insert(
              index,
              {
                ...beneficiary,
                beneficiary: {
                  ...beneficiary.beneficiary,
                  value: {
                    ...beneficiary.beneficiary.value,
                    vat: vatApplied,
                  },
                },
              },
              values.beneficiaries,
            ),
          )
          setBeneficiariesInState(arrayUtils.insert(index, beneficiary, beneficiariesInState))
        }

        const handleBeneficiaryRemoved = (index: number): void => {
          setFieldValue('beneficiaries', arrayUtils.removeByIndex(index, values.beneficiaries))
        }

        const handleBeneficiaryAmountChange = (beneficiaryIndex: number, e: any): void => {
          const beneficiary =
            values.beneficiaries && values.beneficiaries.find((v: any, i: any) => i === beneficiaryIndex)
          if (!beneficiary) return
          setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
            ...beneficiary,
            beneficiary: {
              ...beneficiary.beneficiary,
              value: {
                ...beneficiary.beneficiary.value,
                amount: e.target.value,
              },
            },
          })
        }

        const handleBeneficiaryReferenceChange = (beneficiaryIndex: number, reference: any, type: any): void => {
          const beneficiary = values.beneficiaries.find((v: any, i: any) => i === beneficiaryIndex)
          setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
            ...beneficiary,
            beneficiary: {
              ...beneficiary.beneficiary,
              value: {
                ...beneficiary.beneficiary.value,
                reference,
              },
            },
          })
        }

        const handleInvoiceTypeChange = (data: any): void => {
          setFieldValue('invoiceType', data)
          // @todo remove this hack
          setTimeout(() => setFieldTouched('invoiceType', true))

          if (depositInvoiceTypes.includes(data.value)) {
            const { customer } = values
            const partyName = getPartyNameById(customer.id)
            setFieldValue('isDepositInvoice', true)

            const beneficiary = {
              beneficiary: {
                type: 'DepositBeneficiary',
                value: {
                  amount: values.amount,
                  partyId: customer?.id,
                  beneficiaryTag: 'TenantDepositAccount',
                  reference: `${partyName} ${data.value}`,
                  transfer: true,
                  vat: false,
                },
              },
            }

            setFieldValue('beneficiaries', [beneficiary])
          } else if (defaultAgencyInvoiceTypes.includes(data.value)) {
            setFieldValue('isDepositInvoice', false)
            setIsDefaultAgencyPaymentRule(true)

            const beneficiary = {
              beneficiary: {
                type: 'PartyBeneficiary',
                value: {
                  amount: values.amount,
                  name: currentAgencyName,
                  partyId: currentAgencyId,
                  partyTag: 'Agency',
                  reference: `${data.label}`,
                  vat: getCurrentAgencyGlobalVatEnabled,
                },
              },
            }

            setFieldValue('beneficiaries', [beneficiary])
          } else if (
            values.beneficiaries.some((b: any) => b.beneficiary.partyTag === 'Agency') ||
            !defaultAgencyInvoiceTypes.includes(data.value)
          ) {
            setFieldValue('beneficiaries', [])
          } else {
            if (values.beneficiaries.some((b: any) => b.beneficiary.type === 'DepositBeneficiary')) {
              setFieldValue('beneficiaries', [])
            }
          }

          // Enable VAT if the following conditions are met
          if (invoiceTypes.includes(data.value) && getCurrentAgencyGlobalVatEnabled) {
            setFieldValue('vat', true)
          } else {
            setFieldValue('vat', false)
          }
        }

        const getBeneficiaries = () => pathOr([], ['beneficiaries'], values)

        const handleDepositBeneficiaryTransferChange = (index: number, transfer: any): void => {
          const beneficiary: $TSFixMe = getBeneficiaries().find((v: any, i: any) => i === index)
          const primaryOwnerId =
            getPrimaryLandlordByPortfolioId(values.portfolioId || values.customer?.portfolioId) || undefined
          const partyName = getPartyNameById(values.customer?.id)
          const invoiceTypeName = pathOr('', ['invoiceType', 'label'], values)

          const transformedBeneficiary = transformDepositBeneficiaryForTransferChange(
            beneficiary,
            transfer,
            currentAgencyId,
            partyName,
            invoiceTypeName,
            primaryOwnerId,
          )

          setFieldValue(`beneficiaries[${index}]`, transformedBeneficiary)
        }

        const handleVatChange = (vatApplied: any) => setFieldValue('vat', vatApplied)

        const handleBeneficiaryVatChange = (i: any, vatApplied: any): void => {
          setFieldValue(`beneficiaries[${i}].beneficiary.value.vat`, vatApplied)
        }

        const handleAmountChange = (e: any): void => {
          if (values.isDepositInvoice) {
            handleBeneficiaryAmountChange(0, e)
          }
          if (isDefaultAgencyPaymentRule) {
            handleBeneficiaryAmountChange(0, e)
          }
          setFieldValue('amount', e.target.value)
        }

        const handleDescriptionChange = ({ target }: any) => {
          setFieldValue('description', target.value)
        }

        const getFieldError = formUtils.getFieldError(submitCount, touched, mergeRight(errors, fieldErrors))

        const formLoaderState = isSubmitting
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) || hasApiErrors
          ? 'error'
          : undefined

        const errorText = hasApiErrors
          ? generalErrors.length > 0
            ? generalErrors[0]
            : 'Oops, something went wrong'
          : 'Resolve errors'

        return (
          <div className={styles.root}>
            <SaveChangesModal
              isOpen={savePromptOpen}
              onSave={handleSaveConfirmation}
              onDiscard={handleDiscardConfirmation}
            />
            <section className={styles['invoice-data']}>
              <FormLoader
                onSubmit={handleSubmit}
                state={formLoaderState}
                buttonProps={{ children: 'Send Invoice', disabled: !isValid }}
                successText="Invoice submitted successfully"
                errorText={errorText}
                ButtonContainer={ButtonContainer}
                ButtonContainerProps={{
                  beforeButton: (
                    <Button
                      disabled={!isValid}
                      size="sm"
                      secondary
                      className={styles['save-button']}
                      onClick={() => handleSave(transformFormValues(values, false))}
                    >
                      Save Draft
                    </Button>
                  ),
                }}
                persistErrorMessage={!hasApiErrors}
                isDisabled={isReadonly}
              >
                <FormField>
                  <PartySearch
                    splitByAccount
                    includePortfolioStatus
                    label="Invoice"
                    name="customer"
                    placeholder="Search for a party to invoice..."
                    value={values.customer.text}
                    onResultSelect={(val: any) => {
                      if (val.portfolioId) {
                        dispatch(portfolioApiEvents.portfolio_request(val.portfolioId))
                      }
                      setFieldValue('customer', val)
                    }}
                    // defaultResultSets={defaultResultSets}
                    // onBlur={handleBlur}
                    tags=""
                    error={getFieldError('customer')}
                    focusOnMount
                    disabled={path(['invoiceType', 'value'], initialValues) === 'DepositTopUp' ? true : false}
                  />
                </FormField>

                <PaymentAllocation
                  errors={mergeRight(errors, fieldErrors)}
                  invoiceTypes={invoiceTypesForSelect}
                  invoiceType={values.invoiceType}
                  onInvoiceTypeChange={handleInvoiceTypeChange}
                  amount={values.amount ? values.amount.toString() : ''}
                  onAmountChange={handleAmountChange}
                  beneficiaries={values.beneficiaries.map((b: any) => {
                    const partyId = pathOr('', ['beneficiary', 'value', 'partyId'], b)
                    return {
                      ...b,
                      beneficiary: {
                        ...b.beneficiary,
                        value: {
                          ...b.beneficiary.value,
                          name: getPartyNameById(partyId),
                        },
                      },
                      isDraggable: false,
                    }
                  })}
                  onNewBeneficiaryAdded={handleNewBeneficiary}
                  onBeneficiaryRemoved={handleBeneficiaryRemoved}
                  onNewBeneficiaryAmountChange={handleBeneficiaryAmountChange}
                  onBeneficiaryReferenceChange={handleBeneficiaryReferenceChange}
                  partyTags={partyTags}
                  vatApplied={values.vat}
                  onVatChange={handleVatChange}
                  onBeneficiaryVatChange={handleBeneficiaryVatChange}
                  onDepositBeneficiaryTransferChange={handleDepositBeneficiaryTransferChange}
                  onDescriptionChange={handleDescriptionChange}
                  portfolioId={values?.portfolioId || values?.customer?.portfolioId}
                  customerId={values.customer?.id}
                  customerTag={values.customer?.partyTag}
                />

                <Segment.Group>
                  <Segment horizontal>
                    <Segment.Label icon={<CalendarOff />} text="Payment Due" />
                    <div className={styles['due-date']}>
                      <DatePicker
                        date={values.dueDate ? new Date(values.dueDate) : new Date()}
                        onChange={(date: any) =>
                          setFieldValue('dueDate', format(isDateValid(date) ? date : Date.now(), 'yyyy-MM-dd'))
                        }
                      />
                    </div>
                  </Segment>
                </Segment.Group>
              </FormLoader>
            </section>
          </div>
        )
      }}
    </Formik>
  )
}
export default subscribe(
  [AdhocInvoiceProvider],
  ({
    updateInvoice,
    currentAgencyId,
    fetchInvoiceTypes,
    invoiceTypesForSelect,
    closeInvoice,
    getPartyNameById,
    initialValues,
    onSubmit,
    createInvoice,
    partyTags,
    getPartyAccount,
    isSubmitting,
  }: any) => ({
    updateInvoice,
    currentAgencyId,
    fetchInvoiceTypes,
    invoiceTypesForSelect,
    closeInvoice,
    getPartyNameById,
    initialValues,
    onSubmit,
    createInvoice,
    partyTags,
    getPartyAccount,
    isSubmitting,
  }),
)(AdhocInvoiceForm)
